import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { updatePageSEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { SplitTestPopup, type SplitTestPopupRef } from "@/components/SplitTestPopup";
import { Lock, ChevronRight, X } from "lucide-react";

const questions = [
  {
    question: "You're alone in a house at night. You hear a noise upstairs. What do you do?",
    options: [
      { text: "Investigate immediately — fear is just adrenaline", type: "A" },
      { text: "Call someone first, then go up slowly", type: "B" },
      { text: "Stay frozen, listening for more clues", type: "C" },
      { text: "Leave the house entirely — survival first", type: "D" },
    ],
  },
  {
    question: "Which setting pulls you deepest into a thriller?",
    options: [
      { text: "An isolated cabin in the mountains", type: "C" },
      { text: "A rain-soaked city with no witnesses", type: "A" },
      { text: "A quiet suburban street hiding dark secrets", type: "B" },
      { text: "An old family estate with a complicated past", type: "D" },
    ],
  },
  {
    question: "What makes a thriller villain truly terrifying?",
    options: [
      { text: "You can't tell if they're human or something worse", type: "D" },
      { text: "They're completely rational — and completely wrong", type: "B" },
      { text: "They're someone you trusted", type: "C" },
      { text: "They're always one step ahead", type: "A" },
    ],
  },
  {
    question: "The perfect thriller ending leaves you feeling...",
    options: [
      { text: "Shaken — like the world shifted under your feet", type: "D" },
      { text: "Satisfied, but unsettled — justice came at a cost", type: "B" },
      { text: "Still unsure what was real", type: "C" },
      { text: "Breathless — heart still pounding", type: "A" },
    ],
  },
  {
    question: "Which of these would you most want to read next?",
    options: [
      { text: "A ghost story rooted in grief and trauma", type: "D" },
      { text: "A psychological cat-and-mouse with an unreliable narrator", type: "C" },
      { text: "A fast-paced chase with secrets unraveling every chapter", type: "A" },
      { text: "A slow-burn mystery in a close-knit community", type: "B" },
    ],
  },
];

const results: Record<string, { title: string; description: string; recommendation: string; color: string }> = {
  A: {
    title: "The Adrenaline Junkie",
    description: "You live for the chase. You need momentum, danger, and a protagonist who makes bold — sometimes reckless — choices. You don't just read thrillers; you sprint through them.",
    recommendation: "You'll love Broken Things for its relentless pacing and the way Maggie refuses to look away from what terrifies her.",
    color: "text-orange-400",
  },
  B: {
    title: "The Psychological Detective",
    description: "You dissect every clue, question every motive, and quietly figure out the twist before the reveal. You read between the lines because that's where the real story lives.",
    recommendation: "Broken Things will keep you guessing — the line between grief, guilt, and the supernatural is never quite where you expect it.",
    color: "text-blue-400",
  },
  C: {
    title: "The Unreliable Narrator's Best Friend",
    description: "You're drawn to stories where reality itself is in question. You love not knowing what to trust — including your own instincts as a reader.",
    recommendation: "Broken Things is built for you. Maggie's perception of what's real shifts with every chapter, and so will yours.",
    color: "text-purple-400",
  },
  D: {
    title: "The Dark Romantic",
    description: "You want atmosphere, depth, and stories that live in the space between the supernatural and the deeply human. Loss, longing, and lingering dread are your literary comfort zone.",
    recommendation: "Broken Things was practically written for you — a haunting story about grief, ghosts, and what we carry with us.",
    color: "text-blood-red",
  },
};

function getResult(answers: string[]): string {
  const counts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0 };
  answers.forEach((a) => counts[a]++);
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

export default function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const popupRef = useRef<SplitTestPopupRef>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    updatePageSEO({
      title: "What Kind of Thriller Reader Are You? | Diane Corso Quiz",
      description: "Take Diane Corso's 5-question thriller reader quiz and discover your reader archetype. Unlock a personalized book recommendation and join the Broken Things mailing list.",
      keywords: "thriller reader quiz, horror book quiz, Diane Corso quiz, book personality quiz, Broken Things",
      canonical: "https://www.dianecorsofiction.com/quiz",
      ogImage: "https://www.dianecorsofiction.com/attached_assets/broken things book rev (1)_1756509124921.webp",
    });
  }, []);

  const resultKey = showResult ? getResult(answers) : null;
  const result = resultKey ? results[resultKey] : null;

  const handleSelect = (type: string) => setSelected(type);

  const handleNext = () => {
    if (!selected) return;
    const newAnswers = [...answers, selected];
    setAnswers(newAnswers);
    setSelected(null);
    if (current + 1 < questions.length) {
      setCurrent(current + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleUnlock = () => {
    popupRef.current?.openManual();
  };

  const handleSubscribed = () => {
    setUnlocked(true);
  };

  const progress = ((current) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gothic-black pt-24 pb-16 px-4">
      <SplitTestPopup ref={popupRef} onSubscribed={handleSubscribed} />

      <div className="max-w-2xl mx-auto relative">
        {/* Close Button */}
        <button
          onClick={() => navigate("/")}
          className="absolute -top-6 right-0 text-gray-500 hover:text-gothic-white transition-colors duration-200 p-2"
          aria-label="Close quiz and return to home"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-cinzel font-bold text-blood-red mb-4">
            What Kind of Thriller Reader Are You?
          </h1>
          <p className="text-gray-300 text-lg">
            5 questions. One chilling revelation about your reading soul.
          </p>
        </div>

        {!showResult ? (
          <Card className="bg-gothic-charcoal border-gothic-gray">
            <CardContent className="p-8">
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>Question {current + 1} of {questions.length}</span>
                  <span>{Math.round(progress)}% complete</span>
                </div>
                <Progress value={progress} className="h-1 bg-gray-700 [&>div]:bg-blood-red" />
              </div>

              <h2 className="text-xl font-playfair font-bold text-gothic-white mb-6 leading-relaxed">
                {questions[current].question}
              </h2>

              <div className="space-y-3">
                {questions[current].options.map((option, i) => (
                  <button
                    key={i}
                    onClick={() => handleSelect(option.type)}
                    className={`w-full text-left px-5 py-4 rounded-lg border transition-all duration-200 font-medium ${
                      selected === option.type
                        ? "border-blood-red bg-blood-red/10 text-gothic-white"
                        : "border-gothic-gray bg-gothic-black text-gray-300 hover:border-gray-500 hover:text-gothic-white"
                    }`}
                  >
                    {option.text}
                  </button>
                ))}
              </div>

              <Button
                onClick={handleNext}
                disabled={!selected}
                className="w-full mt-8 bg-blood-red hover:bg-red-700 text-white font-bold py-6 disabled:opacity-30"
              >
                {current + 1 === questions.length ? "Reveal My Result" : "Next Question"}
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-gothic-charcoal border-gothic-gray overflow-hidden">
            <CardContent className="p-8">
              {!unlocked ? (
                <div className="text-center">
                  <Lock className="w-12 h-12 text-blood-red mx-auto mb-4" />
                  <h2 className="text-2xl font-playfair font-bold text-gothic-white mb-2">
                    Your result is ready...
                  </h2>
                  <p className="text-gray-400 mb-6">
                    Join Diane's mailing list to unlock your thriller reader profile, get exclusive updates on the <span className="text-blood-red font-semibold">Broken Things</span> relaunch, and be first in line for giveaways.
                  </p>
                  <Button
                    onClick={handleUnlock}
                    className="bg-blood-red hover:bg-red-700 text-white font-bold px-10 py-6 text-lg uppercase tracking-wider"
                  >
                    Unlock My Result
                  </Button>
                  <p className="text-xs text-gray-600 mt-4">No spam. Unsubscribe anytime.</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-400 uppercase tracking-widest text-sm mb-2">You are...</p>
                  <h2 className={`text-4xl font-cinzel font-bold mb-6 ${result?.color}`}>
                    {result?.title}
                  </h2>
                  <p className="text-blood-red font-medium mb-4">
                    Thank you for taking the quiz! You will find your results in your inbox.
                  </p>
                  <div className="w-16 h-1 bg-blood-red mx-auto mb-6"></div>
                  <p className="text-gray-300 leading-relaxed mb-6 text-lg">
                    {result?.description}
                  </p>
                  <div className="bg-gothic-black rounded-lg p-5 border border-gothic-gray mb-8">
                    <p className="text-sm text-gray-400 uppercase tracking-widest mb-2">Diane's Pick For You</p>
                    <p className="text-gothic-white italic">{result?.recommendation}</p>
                  </div>
                  <Button asChild className="bg-blood-red hover:bg-red-700 text-white font-bold px-8 py-5">
                    <a href="https://a.co/d/1lgwbkz" target="_blank" rel="noopener noreferrer">
                      Get Broken Things on Amazon
                    </a>
                  </Button>
                  <button
                    onClick={() => { setCurrent(0); setAnswers([]); setShowResult(false); setUnlocked(false); setSelected(null); }}
                    className="block w-full mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    Retake the quiz
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
